import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo_medium.png";
import {
  getOnboardingPathByOrigin,
  resolveVerificationOrigin,
  setOnboardingOrigin,
} from "@/lib/onboarding-origin";

const EmailVerification = () => {
  const navigate = useNavigate();
  const { originSlug, token: pathToken } = useParams<{ originSlug?: string; token?: string }>();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const token =
          pathToken ??
          searchParams.get("t") ??
          searchParams.get("token");
        const origin = resolveVerificationOrigin(originSlug, searchParams);
        const onboardingPath = getOnboardingPathByOrigin(origin);

        if (!token) {
          setStatus("error");
          setErrorMessage("Link de confirmação inválido. Token não encontrado.");
          setOnboardingOrigin(origin);
          return;
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/verify-email-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          setStatus("error");
          setErrorMessage(data.error || "Token inválido ou expirado.");
          return;
        }

        setStatus("success");
        localStorage.removeItem("pending_email_confirmation");
        setOnboardingOrigin(origin);
        setTimeout(() => navigate(onboardingPath), 2000);
      } catch (error) {
        console.error("Unexpected error:", error);
        setStatus("error");
        setErrorMessage("Erro inesperado ao verificar e-mail.");
      }
    };

    verifyEmail();
  }, [searchParams, navigate, originSlug, pathToken]);

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-8" />
        </div>

        <Card className="shadow-medium">
          <CardContent className="p-8 text-center">
            {status === "loading" && (
              <>
                <div className="flex justify-center mb-4">
                  <Loader2 className="h-16 w-16 text-primary animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-primary mb-2">
                  Verificando e-mail...
                </h1>
                <p className="text-muted-foreground">
                  Aguarde enquanto confirmamos seu e-mail.
                </p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="flex justify-center mb-4">
                  <CheckCircle2 className="h-16 w-16 text-green-500" />
                </div>
                <h1 className="text-2xl font-bold text-primary mb-2">
                  E-mail confirmado!
                </h1>
                <p className="text-muted-foreground mb-6">
                  Sua conta foi ativada com sucesso. Redirecionando...
                </p>
              </>
            )}

            {status === "error" && (
              <>
                <div className="flex justify-center mb-4">
                  <XCircle className="h-16 w-16 text-destructive" />
                </div>
                <h1 className="text-2xl font-bold text-primary mb-2">
                  Erro na verificação
                </h1>
                <p className="text-muted-foreground mb-6">
                  {errorMessage || "Não foi possível confirmar seu e-mail."}
                </p>
                <div className="space-y-2">
                  <Button
                    onClick={() => {
                      const origin = resolveVerificationOrigin(originSlug, searchParams);
                      navigate(`/auth/confirm-email?origin=${origin}`);
                    }}
                    className="w-full"
                    size="lg"
                  >
                    Reenviar e-mail de confirmação
                  </Button>
                  <Button
                    onClick={() => navigate("/auth")}
                    variant="outline"
                    className="w-full"
                  >
                    Ir para login
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailVerification;
